import { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { Database, FileStack, Layers, Archive } from "lucide-react";

import { DATASETS } from "../datasets";
import DatasetPage from "./DatasetPage";
import ElementalCostPage from "./ElementalCostPage";
import LegacyPricesPanel from "./LegacyPricesPanel";

/**
 * Databank data entry — ONE page for every register.
 *
 * The registers each have a public screen at /databank/register/:dataset with
 * the admin controls built into it, and that is still where a member browses.
 * But somebody at the secretariat with a stack of spreadsheets was expected to
 * know eight URLs to upload them, and the one address that was linked from the
 * admin menu — this one — maintained only the ORIGINAL price list. So this page
 * is now the console: pick a register, and get that register's own screen with
 * its single-entry form, its CSV/Excel import and its categories.
 *
 * NO new screen per register, deliberately. The body is the same DatasetPage,
 * handed its register as a prop instead of reading it off the route, so a
 * register added to config/databank.php appears here with its own columns,
 * its own form fields and its own import the moment it appears in DATASETS.
 *
 * The two that are not `:dataset` registers keep their own panels:
 *   · Elemental analyses are DOCUMENTS, not figures — its own upload form.
 *   · Basic Prices (original) is the pre-rebuild `databank_items` table, still
 *     live, still maintained, and marked as the old one so nobody loads this
 *     year's prices into it by mistake.
 *
 * The choice rides in `?register=`, so a tab can be bookmarked, linked to a
 * colleague, and walked back with the browser's own back button.
 */

type Tab = {
  key: string;
  label: string;
  hint: string;
  kind: "dataset" | "elemental" | "legacy";
};

const TABS: Tab[] = [
  ...DATASETS.map((d) => ({
    key: d.key,
    label: d.label,
    hint: d.blurb,
    kind: "dataset" as const,
  })),
  {
    key: "elemental_costs",
    label: "Elemental Analyses",
    hint: "Cost analyses as documents — upload the file, tag it, and it is searchable.",
    kind: "elemental",
  },
  {
    key: "legacy_prices",
    label: "Basic Prices (original)",
    hint: "The pre-rebuild price list. Still live at /databank; add new figures to Basic Price List instead.",
    kind: "legacy",
  },
];

const DEFAULT_TAB = TABS[0].key;

export default function AdminDatabankPage() {
  const [params, setParams] = useSearchParams();

  // An unknown ?register= falls back rather than rendering nothing — a stale
  // bookmark from a renamed register should land somewhere usable.
  const active = useMemo(() => {
    const asked = params.get("register");
    return TABS.find((t) => t.key === asked) ?? TABS[0];
  }, [params]);

  const select = (key: string) => {
    const next = new URLSearchParams(params);
    key === DEFAULT_TAB ? next.delete("register") : next.set("register", key);
    // Replace, not push: clicking through six registers should not mean six
    // presses of Back to leave the page.
    setParams(next, { replace: true });
  };

  const icon = (kind: Tab["kind"]) =>
    kind === "elemental" ? (
      <FileStack className="w-3.5 h-3.5" />
    ) : kind === "legacy" ? (
      <Archive className="w-3.5 h-3.5" />
    ) : (
      <Layers className="w-3.5 h-3.5" />
    );

  return (
    <div className="max-w-7xl mx-auto p-4 md:p-6 space-y-5">
      {/* ── The switcher ─────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-100 rounded-2xl p-4 md:p-5 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-indigo-50 border border-indigo-100 shrink-0">
            <Database className="w-5 h-5 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <h1 className="font-bold text-gray-900">Databank — Data Entry</h1>
            <p className="text-sm text-gray-500">
              Every register, one entry at a time or a spreadsheet at a time.
            </p>
          </div>
        </div>

        <div className="mt-4 flex flex-wrap gap-2">
          {TABS.map((t) => {
            const on = t.key === active.key;
            return (
              <button
                key={t.key}
                onClick={() => select(t.key)}
                title={t.hint}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-sm font-semibold border transition ${
                  on
                    ? "bg-indigo-600 border-indigo-600 text-white shadow-sm"
                    : t.kind === "dataset"
                    ? "bg-white border-gray-200 text-gray-600 hover:bg-gray-50"
                    : "bg-gray-50 border-gray-200 text-gray-500 hover:bg-gray-100"
                }`}
              >
                {icon(t.kind)} {t.label}
              </button>
            );
          })}
        </div>

        <p className="mt-3 text-[11px] text-gray-400">{active.hint}</p>
      </div>

      {/* ── The register ─────────────────────────────────────────────────────
          Keyed on the register so switching tabs REMOUNTS the body. The screen
          holds filters, a cursor stack and a half-filled form in its own state;
          carrying those across a switch would show one register's filters
          against another register's columns. */}
      {active.kind === "dataset" ? (
        <DatasetPage key={active.key} dataset={active.key} embedded />
      ) : active.kind === "elemental" ? (
        <ElementalCostPage key={active.key} />
      ) : (
        <LegacyPricesPanel key={active.key} />
      )}
    </div>
  );
}
